import { Outlet, useLocation } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { RouteErrorBoundary } from '../components/common/ErrorBoundary'
import { BrandLogo } from '../components/common/BrandLogo'
import { ThemeToggle } from '../components/common/ThemeToggle'

const BRAND_POINTS = [
  'Registrations and enrollment in one place',
  'Class rosters, attendance and report cards',
  'Tuition, payments and family follow-ups',
]

const AUTH_CARD_DESCRIPTIONS: Record<string, string> = {
  '/register/parent': 'Create your parent portal account',
  '/forgot-password': 'Reset your password',
  '/reset-password': 'Choose a new password',
  '/accept-invitation': 'Accept your invitation',
  '/verify-email': 'Confirm your email address',
  '/accept-family-invite': 'Set up your family account',
}

/** Eight-point star lattice, drawn once and tiled at low opacity on the brand panel. */
const STAR_PATTERN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Cg fill='none' stroke='%23fff' stroke-width='1'%3E%3Cpath d='M32 14l5.3 12.7L50 32l-12.7 5.3L32 50l-5.3-12.7L14 32l12.7-5.3z'/%3E%3Crect x='19.3' y='19.3' width='25.4' height='25.4' transform='rotate(45 32 32)'/%3E%3C/g%3E%3C/svg%3E")`

function BrandPanel() {
  return (
    <aside
      className='relative hidden overflow-hidden text-white lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-16'
      style={{
        background:
          'radial-gradient(120% 80% at 0% 0%, hsl(150 40% 26%) 0%, transparent 60%), hsl(158 62% 14%)',
      }}
    >
      <div
        aria-hidden='true'
        className='pointer-events-none absolute inset-0 opacity-[0.06]'
        style={{ backgroundImage: STAR_PATTERN, backgroundSize: '64px 64px' }}
      />
      <img
        src='/brand/maktab-header-horizontal-reversed.png'
        alt='Maktab'
        className='relative h-10 w-auto self-start'
      />
      <div className='relative max-w-md space-y-8'>
        <div className='space-y-4'>
          <h2 className='font-semibold text-[2.25rem] leading-[1.15] tracking-[-0.02em]'>
            Everything your maktab runs on, in one calm place.
          </h2>
          <p className='text-[15px] text-white/70 leading-relaxed'>
            One home for staff, teachers and families at Darul Islah.
          </p>
        </div>
        <ul className='space-y-3'>
          {BRAND_POINTS.map(point => (
            <li key={point} className='flex items-center gap-3 text-[15px] text-white/85'>
              <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-[#C0923B]/25 text-[#E8C27A]'>
                <Check className='h-3.5 w-3.5' strokeWidth={2.5} />
              </span>
              {point}
            </li>
          ))}
        </ul>
      </div>
      <p className='relative text-white/45 text-xs'>
        &copy; {new Date().getFullYear()} Darul Islah &middot; Maktab Management System
      </p>
    </aside>
  )
}

export function AuthLayout() {
  const { pathname } = useLocation()
  const isLoginPage = pathname === '/login' || pathname === '/'

  // The login page doubles as the landing page: brand panel + sign-in column.
  if (isLoginPage) {
    return (
      <div className='grid h-full overflow-auto bg-background lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]'>
        <BrandPanel />
        <div className='relative flex min-h-full items-center justify-center px-5 py-16 sm:px-8'>
          <div className='absolute top-4 right-4 z-10'>
            <ThemeToggle />
          </div>
          <RouteErrorBoundary area='this page'>
            <Outlet />
          </RouteErrorBoundary>
        </div>
      </div>
    )
  }

  return (
    <div className='flex h-full items-center justify-center overflow-auto bg-background p-4'>
      <Card className='relative w-full max-w-md shadow-md'>
        <CardHeader className='space-y-3 pt-7'>
          <BrandLogo variant='header' className='h-9 self-start' />
          <CardTitle className='sr-only'>Maktab</CardTitle>
          <CardDescription>
            {AUTH_CARD_DESCRIPTIONS[pathname] ?? 'Sign in to continue'}
          </CardDescription>
          <div className='absolute top-4 right-4'>
            <ThemeToggle />
          </div>
        </CardHeader>
        <CardContent>
          <RouteErrorBoundary area='this page'>
            <Outlet />
          </RouteErrorBoundary>
        </CardContent>
      </Card>
    </div>
  )
}

export default AuthLayout
